import { Button, Input, Modal } from 'antd';
import axios from 'axios';
import { Fragment, Component, FormEvent } from 'react';
import { AntdTableColumnsType, FormInputType } from '../../../type-definitions';
import {
  ApiErrorType,
  FloorPlanLocationListType,
  LocationListType,
  UserDataType,
} from '../../../type-definitions/api-types';
import {
  getKeysFromEnum,
  handleSorting,
  handleTableSearch,
} from '../../../utils';
import AntdInput from '../../AntdInput';
import AntdTable from '../../AntdTable';
import { radiusInitialFormElement } from '../helpers';
import cloneDeep from 'lodash/cloneDeep';
import { LocationListEnumKeys } from '../../../api-services/api-responses';
import { checkValidation } from '../../../utils/validation';
import { apiCall } from '../../../api-services/api';
import { locationApi } from '../../../api-services/api-list';
import { v4 } from 'uuid';

interface PropsType {
  userData: Partial<UserDataType>;
  showLocationModal: boolean;
  handleAddLocationModal: () => void;
  onLocationSelectModal: (locationData: LocationListType) => void;
  floorPlanLocationList: FloorPlanLocationListType[];
}

interface StateType {
  loading: boolean;
  searchInput: string;
  locationList: LocationListType[];
  initLocationList: LocationListType[];
  radiusFormElement: FormInputType;
  error: ApiErrorType;
}

class AddLocationModal extends Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);

    this.state = {
      loading: true,
      searchInput: '',
      locationList: [],
      initLocationList: [],
      radiusFormElement: { ...radiusInitialFormElement },
      error: {},
    };
  }

  _isMounted = false;
  axiosCancelSource = axios.CancelToken.source();

  componentDidMount() {
    this._isMounted = true;

    this.handleFetchedData();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.axiosCancelSource.cancel('Component Unmounted');
  }

  handleFetchedData = async (radiusFormElem?: FormInputType) => {
    const { radiusFormElement } = this.state;
    const { userData } = this.props;

    let tempRadiusFormElement = radiusFormElement;

    if (radiusFormElem) {
      tempRadiusFormElement = radiusFormElem;
    }

    try {
      const { url, method, params, contentType } = locationApi.getLocations({
        region: 'circle',
        centre: `${userData.lat},${userData.lng}`,
        radius: tempRadiusFormElement.value,
      });
      const response = await apiCall({
        storeToken: userData?.token,
        url,
        method,
        params,
        contentType,
        cancelToken: this.axiosCancelSource.token,
      });
      const result = response?.data;
      if (result.data) {
        const { floorPlanLocationList } = this.props;

        let tempLocationList: LocationListType[] = result.data.map(
          (item: LocationListType) => ({
            ...item,
            uuid: v4(),
          })
        );
        if (floorPlanLocationList.length > 0) {
          tempLocationList = tempLocationList.filter((item) => {
            const temp = floorPlanLocationList.find((el) => {
              return item.locationID === el.locationID;
            });
            if (temp) {
              return null;
            }
            return item;
          });
        }
        this._isMounted &&
          this.setState({
            loading: false,
            locationList: tempLocationList,
            initLocationList: tempLocationList,
          });
      } else {
        this._isMounted &&
          this.setState({
            loading: false,
          });
      }
    } catch (error: any) {
      this._isMounted &&
        this.setState({
          loading: false,
          error,
        });
    }
  };

  inputChangedHandler = (name: 'radiusFormElement', value: string) => {
    const { radiusFormElement } = this.state;
    if (name) {
      radiusFormElement.value = value;
      radiusFormElement.touched = true;
      radiusFormElement.valid = checkValidation(
        value,
        radiusFormElement.validation
      );

      this._isMounted &&
        this.setState({ radiusFormElement, loading: true }, () => {
          this.handleFetchedData(radiusFormElement);
        });
    }
  };

  handleSearch = (event: FormEvent<HTMLInputElement>) => {
    const value = event?.currentTarget?.value;
    const { initLocationList } = this.state;

    const stateData: Partial<StateType> = { searchInput: value };

    let tempInitData = cloneDeep(initLocationList);

    if (value) {
      const tableColumnsKeys = getKeysFromEnum(LocationListEnumKeys);
      const tempData = handleTableSearch({
        data: tempInitData,
        columnList: tableColumnsKeys,
        searchData: value,
      });
      stateData.locationList = tempData;
    } else {
      stateData.locationList = tempInitData;
    }

    this.setState((prev) => ({ ...prev, ...stateData }));
  };

  render() {
    const { showLocationModal, handleAddLocationModal, onLocationSelectModal } =
      this.props;
    const { radiusFormElement, searchInput, loading, locationList } =
      this.state;
    return (
      <Fragment>
        <Modal
          bodyStyle={{ cursor: 'pointer', resize: 'none' }}
          closeIcon={false}
          visible={showLocationModal}
          onCancel={handleAddLocationModal}
          footer={null}
          width={760}>
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-6 col-sm-6">
              <AntdInput
                {...radiusFormElement}
                onInputChanged={this.inputChangedHandler}
              />
            </div>
          </div>
          <div className="row px-1 py-3 justify-content-center">
            <div className="col-lg-6 col-md-6 col-sm-6 text-center">
              <Input
                size="large"
                onChange={this.handleSearch}
                value={searchInput}
                placeholder="Search..."
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="row px-1">
            <div className="col-lg-12">
              <AntdTable
                loading={loading}
                dataSource={locationList}
                columns={getColumns(onLocationSelectModal)}
                pagination={{
                  pageSize: 3,
                  total: locationList.length,
                }}
                customConfig={{ rowKeyValue: 'uuid' }}
              />
            </div>
          </div>
        </Modal>
      </Fragment>
    );
  }
}

export default AddLocationModal;

function getColumns(
  handleLocationSelect: (data: LocationListType) => void
): AntdTableColumnsType<LocationListType>[] {
  return [
    {
      title: 'Location ID',
      dataIndex: 'locationID',
      key: 'locationID',
      sorter: (a: LocationListType, b: LocationListType) =>
        handleSorting(a.locationID, b.locationID),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Location Name',
      dataIndex: 'locationName',
      key: 'locationName',
      sorter: (a: LocationListType, b: LocationListType) =>
        handleSorting(a.locationName, b.locationName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Friendly Name',
      dataIndex: 'friendlyName',
      key: 'friendlyName',
      sorter: (a: LocationListType, b: LocationListType) =>
        handleSorting(a.friendlyName, b.friendlyName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Partner ID',
      key: 'partnerID',
      dataIndex: 'partnerID',
      sorter: (a: LocationListType, b: LocationListType) =>
        handleSorting(a.partnerID, b.partnerID),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Device ID',
      key: 'locationMac',
      dataIndex: 'locationMac',
      sorter: (a: LocationListType, b: LocationListType) =>
        handleSorting(a.locationMac, b.locationMac),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record: LocationListType) => {
        return (
          <Fragment>
            <div className="row">
              <div className="col-2">
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={() => handleLocationSelect?.(record)}>
                  Select
                </Button>
              </div>
            </div>
          </Fragment>
        );
      },
    },
  ];
}
